import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';

const SEOHead = () => {
  const { language, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const baseUrl = window.location.origin;
    const currentPath = location.pathname; // Will be '/' or '/mx'
    const currentUrl = `${baseUrl}${currentPath}`;

    // Update document title
    document.title = t('meta.title');
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', t('meta.description'));

    // Update keywords
    const keywords = language === 'es' 
      ? 'vestidos de quinceañera azul, vestido quinceañera azul cielo, vestido de quinceañera de lujo, vestidos de 15 años azul, vestido azul quinceañera, vestidos quinceañera 2025, vestidos de quinceañera azules'
      : 'blue quinceanera dress, blue quinceanera dresses, sky blue quinceanera dress, luxury quinceanera dress, blue quinceanera gown, quinceanera dresses 2025, light blue quince dress';
    
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', keywords);

    // Update language attribute
    document.documentElement.lang = language;

    // Remove existing hreflang tags
    const existingHreflangs = document.querySelectorAll('link[hreflang]');
    existingHreflangs.forEach(el => el.remove());

    // Add hreflang tags for international SEO
    const hreflangEn = document.createElement('link');
    hreflangEn.setAttribute('rel', 'alternate');
    hreflangEn.setAttribute('hreflang', 'en-US');
    hreflangEn.setAttribute('href', `${baseUrl}/`);
    document.head.appendChild(hreflangEn);

    const hreflangEs = document.createElement('link');
    hreflangEs.setAttribute('rel', 'alternate');
    hreflangEs.setAttribute('hreflang', 'es-MX');
    hreflangEs.setAttribute('href', `${baseUrl}/mx`);
    document.head.appendChild(hreflangEs);

    const hreflangDefault = document.createElement('link');
    hreflangDefault.setAttribute('rel', 'alternate');
    hreflangDefault.setAttribute('hreflang', 'x-default');
    hreflangDefault.setAttribute('href', `${baseUrl}/`);
    document.head.appendChild(hreflangDefault);

    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Update/Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: t('meta.title') },
      { property: 'og:description', content: t('meta.description') },
      { property: 'og:type', content: 'article' },
      { property: 'og:locale', content: language === 'es' ? 'es_MX' : 'en_US' },
      { property: 'og:locale:alternate', content: language === 'es' ? 'en_US' : 'es_MX' },
      { property: 'og:site_name', content: 'Blue Quinceañera Dresses' },
      { property: 'og:url', content: currentUrl },
      { property: 'og:image', content: 'https://trendykool.com/wp-content/uploads/2024/07/kf-Sd3e28b06fe374905a4342f8e7eafee64c-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp' },
      { property: 'og:price:amount', content: '1128.15' },
      { property: 'og:price:currency', content: 'USD' },
    ];

    ogTags.forEach(tag => {
      let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
      if (!ogTag) {
        ogTag = document.createElement('meta');
        ogTag.setAttribute('property', tag.property);
        document.head.appendChild(ogTag);
      }
      ogTag.setAttribute('content', tag.content);
    });

    // Update/Add Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: t('meta.title') },
      { name: 'twitter:description', content: t('meta.description') },
      { name: 'twitter:image', content: 'https://trendykool.com/wp-content/uploads/2024/07/kf-Sd3e28b06fe374905a4342f8e7eafee64c-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp' },
    ];

    twitterTags.forEach(tag => {
      let twitterTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (!twitterTag) {
        twitterTag = document.createElement('meta');
        twitterTag.setAttribute('name', tag.name);
        document.head.appendChild(twitterTag);
      }
      twitterTag.setAttribute('content', tag.content);
    });

    // Add geo tags for regional targeting
    const geoTags = [
      { name: 'geo.region', content: 'US-MX' },
      { name: 'geo.placename', content: language === 'es' ? 'Mexico' : 'United States' },
    ];

    geoTags.forEach(tag => {
      let geoTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (!geoTag) {
        geoTag = document.createElement('meta');
        geoTag.setAttribute('name', tag.name);
        document.head.appendChild(geoTag);
      }
      geoTag.setAttribute('content', tag.content);
    });

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(el => el.remove());

    // Add Article Schema
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t('hero.title'),
      description: t('meta.description'),
      author: {
        '@type': 'Organization',
        name: 'TrendyKool',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Blue Quinceañera Dresses',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': currentUrl,
      },
      keywords: keywords,
      inLanguage: language === 'es' ? 'es-MX' : 'en-US',
    };

    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(articleScript);

    // Add Product Schema
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Luxury Sky Blue Quinceañera Dress',
      description: t('featured.description'),
      image: [
        'https://trendykool.com/wp-content/uploads/2024/07/kf-Sd3e28b06fe374905a4342f8e7eafee64c-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp',
        'https://trendykool.com/wp-content/uploads/2024/07/kf-S1e6e8d41a3fd4916ae6c807c942cb290a-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp',
        'https://trendykool.com/wp-content/uploads/2024/07/kf-S7d231c0df2704791a767268c566fe465y-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep-760x760.webp',
        'https://trendykool.com/wp-content/uploads/2024/07/kf-S77a38d6f1fd8487ca5c7cd203b4dabd5K-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep-150x150.webp',
        'https://trendykool.com/wp-content/uploads/2024/07/kf-S5310a96b8fdb439c9faa7256745c95f27-Luxury-Sky-Blue-Off-the-Shoulder-Quinceanera-Appliques-Lace-Beads-2024-Ball-Gown-Floor-Length-Sweep.webp',
      ],
      brand: {
        '@type': 'Brand',
        name: 'TrendyKool',
      },
      offers: {
        '@type': 'Offer',
        url: 'https://trendykool.com/product/sky-blue-quinceanera-dresses/',
        priceCurrency: 'USD',
        price: '1128.15',
        priceValidUntil: '2025-12-31',
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: {
          '@type': 'Organization',
          name: 'TrendyKool',
        },
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: 'USD',
          },
          shippingDestination: {
            '@type': 'DefinedRegion',
            addressCountry: ['US', 'MX'],
          },
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
      },
      category: 'Quinceañera Dresses',
    };

    const productScript = document.createElement('script');
    productScript.type = 'application/ld+json';
    productScript.textContent = JSON.stringify(productSchema);
    document.head.appendChild(productScript);

    // Add FAQ Schema
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: t('faq.q1'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t('faq.a1'),
          },
        },
        {
          '@type': 'Question',
          name: t('faq.q2'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t('faq.a2'),
          },
        },
        {
          '@type': 'Question',
          name: t('faq.q3'),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t('faq.a3'),
          },
        },
      ],
    };

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    // Add Organization Schema
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Blue Quinceañera Dresses',
      url: baseUrl,
      logo: `${baseUrl}/logo192.png`,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Service',
        availableLanguage: ['English', 'Spanish'],
      },
    };

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(orgScript);

    // Add WebSite Schema
    const websiteSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Blue Quinceañera Dresses',
      url: baseUrl,
      inLanguage: language === 'es' ? 'es-MX' : 'en-US',
    };

    const websiteScript = document.createElement('script');
    websiteScript.type = 'application/ld+json';
    websiteScript.textContent = JSON.stringify(websiteSchema);
    document.head.appendChild(websiteScript);

    // Add BreadcrumbList Schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: language === 'es' ? 'Inicio' : 'Home',
          item: baseUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: language === 'es' ? 'Vestidos de Quinceañera' : 'Quinceañera Dresses',
          item: currentUrl,
        },
      ],
    };

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

  }, [language, t, location.pathname]);

  return null;
};

export default SEOHead;
